import React, { useCallback, useContext, useEffect } from 'react';
import style from './style';
import commonStyle from '../../style';
import clsx from 'clsx';
import { FormGroup, FormLabel, MenuItem, TextField } from '@mui/material';
import i18next from 'i18next';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { DefaultSelectProps } from 'services/common/Constants';
import Attribute from './Attribute';
import EADatePicker from '../../../../../components/common/EADatePicker';
import { isContextInstanceNameAvailable } from '../../../../../services/edge/ContexteService';
import { Context } from '../../../../../states/Store';
import moment from 'moment/moment';

const ContextAttributes = ({
  contextModels,
  setSelectedContextModel,
  selectedContextModel,
  formSelector = '',
}) => {
  const { classes } = style();
  const { classes: mainStyleClasses } = commonStyle();
  const [state] = useContext(Context);

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();
  const formMethods = useFormContext();

  const {
    append: appendContextAttribute,
    remove: removeContextAttribute,
  } = useFieldArray({
    control,
    name: `${formSelector}.contextAttributes`,
  });

  const setContextAttributes = useCallback((attribute) => {
    const currentAttributes = watch(`${formSelector}.contextAttributes`) || [];
    const index = currentAttributes.findIndex(
      (attr) => attr.label === attribute.label
    );
    const parsedAttribute = {
      label: attribute.label,
      values: Array.isArray(attribute.values)
        ? typeof attribute.values[0] === 'string'
          ? attribute.values
          : (() => {
            const hasDefault = attribute.values.some(v => 'defaultChoice' in v);
            if (!hasDefault) return attribute.values;
            const defaultItem = attribute.values.find(v => v.defaultChoice && v.value);
            return defaultItem ? [defaultItem.value] : null;
          })()
        : null
    }

    if (index !== -1) {
      removeContextAttribute(index);
      appendContextAttribute(parsedAttribute);
    } else {
      appendContextAttribute(parsedAttribute);
    }
  }, [removeContextAttribute, appendContextAttribute, watch, formSelector]);


  const contextAttributes = watch(`${formSelector}.contextAttributes`);

  useEffect(() => {
    selectedContextModel && !contextAttributes.length &&
      selectedContextModel.contextAttributes.forEach((contextAttribute) => {
        setContextAttributes(contextAttribute);
      });
  }, [selectedContextModel, contextAttributes.length, setContextAttributes]);


  const isAttributeError = (attributeId)=> {
    if(errors['attributes']) {
      const isError = errors['attributes'].find((err) => err && err.attributeId === attributeId);
      return isError&&<span className={classes.errorMessage}>{i18next.t('infos.required')}</span>
    }
    return false
  }

  const attributeSorting = (a, b) => a.label.localeCompare(b.label)


  return (
    <div className={classes.root}>
      <div className={classes.firstBlock}>
        <div className={clsx(mainStyleClasses.inputWrapper, classes.item)}>
          <FormLabel className={mainStyleClasses.formLabel}>
            {`${i18next.t('context.creationInstance.context')} * `}
          </FormLabel>
          <FormGroup>
            <Controller
              control={control}
              name={`${formSelector}.contextModelName`}
              rules={{
                required: i18next.t('infos.required'),
              }}
              render={({ field }) => (
                <TextField
                  id={`contextModelName`}
                  variant="outlined"
                  select
                  error={!!errors?.[formSelector]?.contextModelName}
                  helperText={
                    errors?.[formSelector]?.contextModelName?.message || ''
                  }
                  {...field}
                  className={clsx(mainStyleClasses.textField)}
                  onChange={(e) => {
                    const selectedModel = contextModels.find(
                      (c) => c.name === e.target.value
                    );
                    setSelectedContextModel(selectedModel);
                    field.onChange(e);
                  }}
                  SelectProps={{
                    ...DefaultSelectProps,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) {
                        return (
                          <span className={classes.placeholder}>
                            {i18next.t(
                              `thirdpartySheet.contextTp.selectContextPlaceholder`
                            )}
                          </span>
                        );
                      }
                      return <span>{selected}</span>;
                    },
                  }}
                >
                  {contextModels.map((model) => (
                    <MenuItem key={model.name} value={model.name}>
                      {model.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </FormGroup>
        </div>
        <div className={clsx(mainStyleClasses.inputWrapper, classes.item)}>
          <FormLabel className={mainStyleClasses.formLabel}>
            {`${i18next.t('context.creationInstance.reference')} * `}
          </FormLabel>
          <FormGroup>
            <Controller
              control={control}
              name={`${formSelector}.reference`}
              rules={{
                required: i18next.t('infos.required'),
                validate: async (value) => {
                  if (value.length > 100) {
                    return i18next.t(
                      'demandes.contextModel.creationForm.config.labelLengthError'
                    );
                  }
                  if (selectedContextModel) {
                    const isNameAvailable = await isContextInstanceNameAvailable(
                      state.account.id,
                      value,
                      selectedContextModel.id
                    );
                    if (!isNameAvailable) {
                      return i18next.t('context.creationInstance.nameError');
                    }
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  id="reference"
                  className={clsx(mainStyleClasses.textField)}
                  placeholder={i18next.t(
                    'context.creationInstance.referencePlaceholder'
                  )}
                  variant="outlined"
                  error={!!errors?.[formSelector]?.reference}
                  helperText={errors?.[formSelector]?.reference?.message || ''}
                  fullWidth
                  {...field}
                />
              )}
            />
          </FormGroup>
        </div>
        <div className={clsx(mainStyleClasses.inputWrapper, classes.item)}>
          <FormLabel className={mainStyleClasses.formLabel}>
            {`${i18next.t('context.creationInstance.expirationDate')}`}
          </FormLabel>
          <FormGroup>
            <Controller
              control={control}
              name={`${formSelector}.expirationDate`}
              render={({ field }) => (
                <EADatePicker
                  {...field}
                  minDate={moment().add(1, "days")}
                  formMethods={formMethods}
                  name={`${formSelector}.expirationDate`}
                  isRequired={true}
                  onlyDate
                  sxClass={{
                    marginTop: '8px',
                  }}
                  onError={() => {}}
                  ref={null}
                />
              )}
            />
          </FormGroup>
        </div>
      </div>
      <div className={clsx(mainStyleClasses.textInputWrapper, classes.item)}>
        <FormLabel className={mainStyleClasses.formLabel}>
          {`${i18next.t('context.creationInstance.description')}`}
        </FormLabel>
        <FormGroup>
          <Controller
            control={control}
            name={`${formSelector}.description`}
            rules={{
              validate: (value) => {
                if (value.length > 1000) {
                  return i18next.t(
                    'demandes.contextModel.creationForm.config.descriptionHelp'
                  );
                }
                return true;
              },
            }}

            render={({ field }) => (
              <TextField
                id="name"
                className={clsx(mainStyleClasses.textField)}
                placeholder={i18next.t(
                  'context.creationInstance.descriptionPlaceholder'
                )}
                variant="outlined"
                error={!!errors?.[formSelector]?.description}
                fullWidth
                multiline
                rows={4}
                helperText={!!errors?.[formSelector]?.description ? errors?.[formSelector]?.description.message : ''}
                {...field}
              />
            )}
          />
          <p className={classes.inputHelper}>{i18next.t("demandes.contextModel.creationForm.config.descriptionHelp")}</p>

        </FormGroup>
      </div>
      {selectedContextModel && selectedContextModel.contextAttributes.length ? (
        <div className={classes.attributeList}>
          <div className={classes.attributeListHeader}>
            <span className={classes.attributeListHeaderTitle}>
              {i18next.t(`attributesLabel`)}
            </span>
          </div>
          <div className={classes.attributeListContent}>
            {selectedContextModel.contextAttributes.sort(attributeSorting).map((contextAttribute,i) => (
              <div key={contextAttribute.id}>
                <Attribute
                  contexteAttribute={contextAttribute}
                  onValidateSuccess={setContextAttributes}
                  currentValue={contextAttributes.find(
                    (c) => c.label === contextAttribute.label
                  )}
                />
                {isAttributeError(contextAttribute.id)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ContextAttributes;
